import React, { useState } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Upload, FileUp, CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Configuration
const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks
const MAX_STANDARD_SIZE = 500 * 1024 * 1024; // 500MB - use standard upload below this

/**
 * ChunkedFileUploader - Handles both standard and chunked uploads
 * 
 * For files <= 500MB: Uses standard multipart upload
 * For files > 500MB: Uses chunked upload with progress tracking
 */
function ChunkedFileUploader({ 
  companyId, 
  moduleType, 
  token, 
  onUploadComplete, 
  onUploadError,
  disabled = false 
}) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setUploadStatus('idle');
    setErrorMessage('');
  };

  const uploadStandard = async (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await axios.post(
      `${API_BASE_URL}/api/companies/${companyId}/modules/${moduleType}/agent00/ingest`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      }
    );

    return response.data;
  };

  const uploadChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // Step 1: Initialize upload
    setCurrentFile(`Inicializando: ${file.name}`);
    const initResponse = await axios.post(
      `${API_BASE_URL}/api/companies/${companyId}/uploads/init`,
      {
        filename: file.name,
        total_size: file.size,
        total_chunks: totalChunks,
        module_type: moduleType
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const uploadId = initResponse.data.upload_id;

    // Step 2: Upload chunks
    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
      const start = chunkNumber * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const chunkFormData = new FormData();
      chunkFormData.append('file', chunk, `chunk_${chunkNumber}`);

      setCurrentFile(`Subiendo ${file.name}: chunk ${chunkNumber + 1}/${totalChunks}`);
      
      await axios.post(
        `${API_BASE_URL}/api/companies/${companyId}/uploads/${uploadId}/chunk/${chunkNumber}`,
        chunkFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const progress = Math.round(((chunkNumber + 1) / totalChunks) * 80); // 80% for chunks
      setUploadProgress(progress);
    }

    // Step 3: Complete upload
    setCurrentFile(`Ensamblando: ${file.name}`);
    setUploadProgress(85);
    
    await axios.post(
      `${API_BASE_URL}/api/companies/${companyId}/uploads/${uploadId}/complete`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    // Step 4: Process the file
    setCurrentFile(`Procesando: ${file.name}`);
    setUploadProgress(90);

    const processResponse = await axios.post(
      `${API_BASE_URL}/api/companies/${companyId}/modules/${moduleType}/agent00/ingest-from-upload?upload_id=${uploadId}`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    setUploadProgress(100);
    return processResponse.data;
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !moduleType) return;

    setUploadStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');

    try {
      // Check if any file is too large for standard upload
      const largeFiles = selectedFiles.filter(f => f.size > MAX_STANDARD_SIZE);
      const smallFiles = selectedFiles.filter(f => f.size <= MAX_STANDARD_SIZE);

      let results = [];

      // Upload small files with standard method
      if (smallFiles.length > 0) {
        setCurrentFile(`Subiendo ${smallFiles.length} archivo(s) estándar...`);
        const standardResult = await uploadStandard(smallFiles);
        results.push(standardResult);
      }

      // Upload large files with chunked method
      for (const file of largeFiles) {
        setCurrentFile(`Subiendo archivo grande: ${file.name} (${formatFileSize(file.size)})`);
        const chunkedResult = await uploadChunked(file);
        results.push(chunkedResult);
      }

      setUploadStatus('success');
      setCurrentFile(null);
      
      // Return the last result (or combine them if needed)
      if (onUploadComplete) {
        onUploadComplete(results[results.length - 1]);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      const errorMsg = error.response?.data?.detail || error.message || 'Error al subir archivos';
      setErrorMessage(errorMsg);
      
      if (onUploadError) {
        onUploadError(errorMsg);
      }
    }
  };

  const getFileSizeWarning = () => {
    const largeFiles = selectedFiles.filter(f => f.size > MAX_STANDARD_SIZE);
    if (largeFiles.length > 0) {
      return (
        <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-700/30 rounded-lg flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-400">
            <p className="font-medium">Archivos grandes detectados</p>
            <p className="text-yellow-500/80 mt-1">
              {largeFiles.map(f => f.name).join(', ')} ({formatFileSize(largeFiles.reduce((acc, f) => acc + f.size, 0))})
              <br />
              Se usará upload por chunks. Esto puede tomar varios minutos.
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* File Drop Zone */}
      <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-gray-500 transition-colors">
        <FileUp className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <input
          type="file"
          multiple
          accept=".csv,.xlsx,.xls,.pdf"
          onChange={handleFileChange}
          className="hidden"
          id="chunked-file-upload"
          disabled={disabled || uploadStatus === 'uploading'}
        />
        <label htmlFor="chunked-file-upload" className="cursor-pointer">
          <Button 
            variant="outline" 
            onClick={() => document.getElementById('chunked-file-upload').click()}
            disabled={disabled || uploadStatus === 'uploading'}
          >
            <Upload className="mr-2 h-4 w-4" />
            Seleccionar Archivos
          </Button>
        </label>
        <p className="text-xs text-gray-500 mt-2">
          CSV, Excel, PDF • Sin límite de tamaño
        </p>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-300">Archivos seleccionados:</p>
          {selectedFiles.map((file, index) => (
            <div 
              key={index} 
              className="text-sm text-gray-400 flex items-center gap-2 p-2 bg-gray-800/50 rounded"
            >
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="truncate flex-1">{file.name}</span>
              <span className="text-gray-500 flex-shrink-0">
                {formatFileSize(file.size)}
              </span>
              {file.size > MAX_STANDARD_SIZE && (
                <span className="text-xs bg-yellow-900/50 text-yellow-400 px-2 py-0.5 rounded">
                  GRANDE
                </span>
              )}
            </div>
          ))}
          {getFileSizeWarning()}
        </div>
      )}

      {/* Upload Progress */}
      {uploadStatus === 'uploading' && (
        <div className="space-y-2 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="text-blue-400 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {currentFile || 'Procesando...'}
            </span>
            <span className="text-blue-300 font-medium">{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}

      {/* Error Message */}
      {uploadStatus === 'error' && errorMessage && (
        <div className="p-4 bg-red-900/20 border border-red-700/30 rounded-lg flex items-start gap-2">
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-400">
            <p className="font-medium">Error al subir archivos</p>
            <p className="text-red-500/80 mt-1">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {uploadStatus === 'success' && (
        <div className="p-4 bg-green-900/20 border border-green-700/30 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <span className="text-sm text-green-400">Archivos procesados correctamente</span>
        </div>
      )}

      {/* Upload Button */}
      <Button 
        onClick={handleUpload} 
        disabled={disabled || uploadStatus === 'uploading' || selectedFiles.length === 0 || !moduleType}
        className="w-full"
        size="lg"
      >
        {uploadStatus === 'uploading' ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Subiendo... {uploadProgress}%
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            Procesar Archivos y Seleccionar Agentes
          </>
        )}
      </Button>
    </div>
  );
}

export default ChunkedFileUploader;
